import { Segmented } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { useSearchHistory } from '@/features/project/_hooks/search-projects/local-storage';
import { t } from '@/i18n';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { z } from 'zod';

const projectsSegment = z.enum([
  'project',
  'group',
  'organization-template',
  'projectInspection',
]);
const projectsSegmentLabelModifier = {
  project: t('project'),
  group: t('project-group-list'),
  'organization-template': t('organization-template'),
  projectInspection: t('project-inspection'),
} as const satisfies Record<z.infer<typeof projectsSegment>, string>;

type Props = {
  segment: z.infer<typeof projectsSegment>;
};

export const SegmentSwitcher = ({ segment }: Props) => {
  const navigate = useNavigate();
  const { lastSearched, lastSearchedQueryParams } = useSearchHistory();
  const projectFiscalYear = lastSearched?.fiscalYear;
  const me = useMe();

  const options = useMemo(() => {
    const hasTemplateAbility = me.data?.role.templateAbility ?? false;
    return projectsSegment.options
      .filter((value) => {
        if (value === 'organization-template' && !hasTemplateAbility) {
          return false;
        }
        return true;
      })
      .map((value) => ({
        label: projectsSegmentLabelModifier[value],
        value,
      }));
  }, [me.data?.role.templateAbility]);

  return (
    <div>
      <Segmented
        value={segment}
        onChange={(value) => {
          if (value === 'project') {
            navigate(`/rs/project?${lastSearchedQueryParams.toString()}`, {
              replace: true,
            });
          } else if (value === 'group') {
            navigate(
              `/rs/project/group${
                projectFiscalYear ? `?fiscalYear=${projectFiscalYear}` : ''
              }`,
              {
                replace: true,
              },
            );
          } else if (value === 'organization-template') {
            navigate('/rs/project/organization-templates', {
              replace: true,
            });
          } else if (value === 'projectInspection') {
            navigate('/rs/project/inspection', {
              replace: true,
            });
          }
        }}
        options={options}
      />
    </div>
  );
};
